import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRightIcon, ArrowUpRightIcon } from "lucide-react";
import { CtaLink } from "../ui/CtaLink";
import { HERO_FACTS, SITE } from "../../data/site";
export function Hero() {
  const reduce = useReducedMotion();
  const enter = (delay: number) => ({
    initial: reduce ? false : {
      opacity: 0,
      y: 16
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.3,
      delay,
      ease: [0.23, 1, 0.32, 1]
    }
  });
  return <section className="relative -mt-[clamp(68px,5vw,84px)] overflow-hidden pt-[clamp(68px,5vw,84px)]" data-id="element-146" data-mp="5oxkslxsrwoq">
      <div className="grid-lines pointer-events-none absolute inset-0" aria-hidden data-id="element-147" data-mp="pugj3pn5uht" />
      <div className="blue-glow pointer-events-none absolute inset-0" aria-hidden data-id="element-148" data-mp="3rwx65qptzspx" />
      <div className="shell relative grid items-center gap-[clamp(2.5rem,5vw,7rem)] py-[clamp(3rem,7vw,9rem)] lg:grid-cols-[1.15fr_0.85fr]" data-id="element-149" data-mp="3rz1f4n2l7hx2">
        <div data-id="element-150" data-mp="1q6db4bi4cqg8">
          <motion.p {...enter(0)} className="t-eyebrow mb-8 flex items-center gap-3 text-muted" data-id="element-151" data-mp="1q6lmfyylfnpb">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" data-id="element-152" data-mp="1mvjf437z89vu" /> Web design &amp; development studio
          </motion.p>
          <motion.h1 {...enter(0.04)} className="t-display max-w-[13ch]" data-id="element-153" data-mp="1rtchel65sjqu">
            Your business deserves a{" "}
            <span className="relative inline-block text-accent" data-id="element-154" data-mp="1d0iysqvlbnhy">
              better
              <svg className="absolute -bottom-[0.08em] left-0 w-full" viewBox="0 0 200 8" preserveAspectRatio="none" aria-hidden data-id="element-155" data-mp="1hzc6u3s3ymaq">
                <path d="M2 6 C 50 1, 150 1, 198 5" stroke="#0A57FF" strokeWidth="3" fill="none" strokeLinecap="round" data-id="element-156" data-mp="2amgekgil5eq7" />
              </svg>
            </span>{" "}
            website.
          </motion.h1>
          <motion.p {...enter(0.08)} className="t-lead mt-8 max-w-[48ch] text-fg-2" data-id="element-157" data-mp="20vlp73a1lz0j">
            We build modern, professional websites that help businesses stand out, attract customers, and grow online.
          </motion.p>
          <motion.div {...enter(0.12)} className="mt-10 flex flex-wrap gap-3" data-id="element-158" data-mp="2td6j09nt19l6">
            <CtaLink to="/contact" track="hero_build" size="lg" data-id="element-159" data-mp="338n5l5hipj9n">
              Build My Website <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" data-id="element-160" data-mp="2y9k1bn9d3h2q" />
            </CtaLink>
            <CtaLink to="/demo" variant="ghost" track="hero_explore" size="lg" data-id="element-161" data-mp="2u65hsmm7sikj">
              Explore Our Design <ArrowUpRightIcon className="h-4 w-4" data-id="element-162" data-mp="2p83zw50iypan" />
            </CtaLink>
          </motion.div>
          <motion.dl {...enter(0.16)} className="mt-12 grid max-w-[680px] grid-cols-1 overflow-hidden rounded-2xl border border-line bg-ink-2/60 backdrop-blur sm:grid-cols-3" data-id="element-163" data-mp="2kb3tfszrjkj0">
            {HERO_FACTS.map((f, i) => <div key={f.label} className={`flex items-baseline justify-between gap-4 p-[clamp(0.85rem,1.4vw,1.5rem)] sm:block ${i > 0 ? "border-t border-line sm:border-l sm:border-t-0" : ""}`} data-id="element-164" data-mp="2ahmzmozmm5fc" data-mpi={i}>
                <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted" data-id="element-165" data-mp="21i9s41v2bqx9">{f.label}</dt>
                <dd className="mt-2 font-mono text-[clamp(0.85rem,0.8rem+0.3vw,1.1rem)] text-fg" data-id="element-166" data-mp="1wm1cyogv7xq3">{f.value}</dd>
              </div>)}
          </motion.dl>
        </div>

        <motion.figure initial={reduce ? false : {
        opacity: 0,
        scale: 0.97
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        delay: 0.1,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative overflow-hidden rounded-[clamp(1rem,1.5vw,1.75rem)] border border-line bg-black" data-id="element-167" data-mp="gzfniikpkucs">
          <img src={SITE.heroImage} alt="Glass and chrome sculpture lit in electric blue" className="h-[clamp(380px,72vh,1100px)] w-full object-cover" data-id="element-168" data-mp="3ufwtvfrj8gmh" />
          <figcaption className="absolute inset-x-0 bottom-0 flex items-end justify-between bg-black/40 p-[clamp(1rem,1.6vw,1.75rem)] backdrop-blur-sm" data-id="element-169" data-mp="72t2ntsdu3ar">
            <span data-id="element-170" data-mp="36pjb6fuu7u4k">
              <span className="t-eyebrow block text-muted" data-id="element-171" data-mp="36q2kj86f9a6z">Studio output</span>
              <span className="mt-1 block text-fg" data-id="element-172" data-mp="3aql98td0q04y">Design · Build · Host</span>
            </span>
            <span className="rounded-full border border-line-strong px-3 py-1 font-mono text-[11px] tracking-[0.2em] text-fg-2" data-id="element-173" data-mp="2ro2qfew8vgb8">NXR</span>
          </figcaption>
        </motion.figure>
      </div>
    </section>;
}